// Persistência do widget flutuante de chat: aberto/fechado, colaborador ativo
// e conversa ativa sobrevivem a um refresh do navegador (F5). Mesmo padrão de
// chaveamento por usuário+organização do useSelectedWorker, para o estado não
// vazar entre contas ou organizações no mesmo navegador. Sem localStorage
// (modo privado/cota), tudo degrada silenciosamente para o comportamento
// anterior: conversa nova a cada refresh.

const WIDGET_STATE_STORAGE_PREFIX = 'wfn-widget'

export interface WidgetStateStorageContext {
  userId: number | string | null | undefined
  organizationId: string | null | undefined
}

export interface PersistedWidgetState {
  open: boolean
  workerId: string | null
  sessionId: string | null
  // Quando a conversa ativa é uma edição de Processo, o id do processo
  // acompanha: depois de um refresh, reabrir a edição do MESMO processo
  // (botão ou deep-link ?edit_chat=1) reaproveita a conversa restaurada em
  // vez de criar outro kickoff.
  editProcessId: string | null
}

function widgetStateStorageKey(context: WidgetStateStorageContext): string | null {
  if (!context.userId || !context.organizationId) return null

  return `${WIDGET_STATE_STORAGE_PREFIX}:${context.userId}:${context.organizationId}`
}

export function readWidgetState(context: WidgetStateStorageContext): PersistedWidgetState | null {
  const key = widgetStateStorageKey(context)
  if (!key) return null

  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null

    const candidate = parsed as Record<string, unknown>
    return {
      open: candidate.open === true,
      workerId: typeof candidate.workerId === 'string' ? candidate.workerId : null,
      sessionId: typeof candidate.sessionId === 'string' ? candidate.sessionId : null,
      editProcessId: typeof candidate.editProcessId === 'string' ? candidate.editProcessId : null,
    }
  } catch {
    return null
  }
}

export function persistWidgetState(state: PersistedWidgetState, context: WidgetStateStorageContext) {
  const key = widgetStateStorageKey(context)
  if (!key) return

  try {
    localStorage.setItem(key, JSON.stringify(state))
  } catch {
    // Storage indisponível: o estado só não persiste.
  }
}

export function clearWidgetSession(context: WidgetStateStorageContext) {
  const current = readWidgetState(context)
  if (!current) return

  persistWidgetState({ ...current, sessionId: null }, context)
}
